import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { type SelectChangeEvent } from '@mui/material/Select';

import ListFilterToolbar from 'src/common/components/list-filters/ListFilterToolbar';
import ToolbarFilterSelect from 'src/common/components/list-filters/ToolbarFilterSelect';
import { LanguageKeys } from 'src/common/constants/languages';
import { TrackMapSortBy } from 'src/helpers/filterTrackMaps';
import { hasActiveListFilter, hasActiveSelectFilter } from 'src/helpers/listFilters';
import { isPublished, PublishedFilter } from 'src/helpers/publishedFilter';
import { EOrder } from 'src/interfaces/Order';
import { EView } from 'src/interfaces/View';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface Props {
  query: string;
  setQuery: (value: string) => void;
  distanceFilter: string | null;
  setDistanceFilter: (value: string | null) => void;
  publishedFilter: PublishedFilter;
  setPublishedFilter: (value: PublishedFilter) => void;
  order: EOrder;
  orderByKey: TrackMapSortBy;
  setOrder: (order: EOrder) => void;
  setOrderByKey: (key: TrackMapSortBy) => void;
  view: EView;
  onSetView: (view: EView) => void;
}

const TrackMapListFilterForm = ({
  query,
  setQuery,
  distanceFilter,
  setDistanceFilter,
  publishedFilter,
  setPublishedFilter,
  order,
  orderByKey,
  setOrder,
  setOrderByKey,
  view,
  onSetView,
}: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const { distances } = useSystemOptionsStore(s => s);
  const publishedDistances = useMemo(
    () => distances.filter(distance => isPublished(distance)),
    [distances],
  );

  const sortOptions = useMemo(
    () => [
      { label: t('system.trackMaps.nameLabel'), value: 'name' },
      { label: t('system.trackMaps.fileName'), value: 'fileName' },
      { label: t('system.trackMaps.routeLengthLabel'), value: 'totalDistance' },
      { label: t('system.trackMaps.distance'), value: 'distance' },
    ],
    [t],
  );

  return (
    <ListFilterToolbar
      popupId="trackMapFilters"
      searchQuery={query}
      onSearchChange={setQuery}
      searchPlaceholder={t('system.listFilters.searchPlaceholder')}
      hasActiveFilters={hasActiveListFilter(distanceFilter) || hasActiveSelectFilter(publishedFilter)}
      filterPopoverContent={
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid size={12}>
            <ToolbarFilterSelect
              id="track-map-distance-filter-label"
              label={t('events.distance')}
              value={distanceFilter ?? ''}
              onChange={v => setDistanceFilter(v || null)}
              renderSelected={selected => {
                const distance = publishedDistances.find(d => d.id === selected);
                return distance ? distance.i18n[currentLang] : selected;
              }}
            >
              <MenuItem value="">{t('base.all')}</MenuItem>
              {publishedDistances.map(distance => (
                <MenuItem key={distance.id} value={distance.id}>
                  {distance.i18n[currentLang]}
                </MenuItem>
              ))}
            </ToolbarFilterSelect>
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth size="small">
              <InputLabel id="track-map-published-filter-label" shrink>
                {t('system.sponsors.publishedStatus')}
              </InputLabel>
              <Select
                labelId="track-map-published-filter-label"
                value={publishedFilter}
                label={t('system.sponsors.publishedStatus')}
                onChange={(e: SelectChangeEvent<PublishedFilter>) =>
                  setPublishedFilter(e.target.value as PublishedFilter)
                }
              >
                <MenuItem value="all">{t('base.all')}</MenuItem>
                <MenuItem value="true">{t('system.sponsors.published')}</MenuItem>
                <MenuItem value="false">{t('system.sponsors.unpublished')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      }
      order={order}
      orderByKey={orderByKey}
      setOrder={setOrder}
      setOrderByKey={key => setOrderByKey(key as TrackMapSortBy)}
      sortOptions={sortOptions}
      view={view}
      onSetView={onSetView}
    />
  );
};

export default TrackMapListFilterForm;
